<template lang="pug">
	.timeline-message(
		:class="messageClasses"
	)

		//- Editing actions
		.buttons.are-small(v-if="editable")
			button.button.is-primary(@click="$emit('edit')")
				b-icon(icon="pen")
			button.button(@click="$emit('toggle')", :class="{'is-success': public}")
				b-icon(icon="eye", v-if="public")
				b-icon(icon="eye-slash" v-else)
			button.button.draggable-handle
				b-icon(icon="arrows-v")
			button.button.is-danger(@click="$emit('delete')")
				b-icon(icon="trash")

		//- Timeline Dot
		span.timeline-icon

		//- Message content
		.message-wrapper
			.left-column
				//- Time
				p.time(
					v-if="time"
					:class="{'is-overtime': time.isOvertime}"
				)
					span.minutes {{ !time.isOvertime && time.seconds >= 0 ? time.minutes + 1: time.minutes }}
					span.overtime(v-if="time.isOvertime") +{{ time.seconds >= 0 ? time.overtimeMinutes + 1 : this.overtimeMinutes }}
					span.minute-indicator '

				p.action-icon.has-text-centered(v-if="hasIcon")
					img(:src="actionIcon", width="32", height="32")

			.right-column
				//- Content
				article.message-content
					.content
						h4(v-if="headline") {{ headline }}
						p.message-text(v-html="messageWithLinebreaks" v-linkified)

					.video-assist(v-if="action && action === 'video-assist'")
						img(src="../assets/img/video-assist-wide.svg")

					timeline-message-media(
						v-if="media && media.length"
						:action="action"
						:media="media"
					)

					timeline-message-players(
						v-if="players && players.length"
						:action="action"
						:players="players"
						:crest="crest"
					)

				sponsored-by(
					v-if="action && action === 'goal' && goalSponsor && isMainClub"
					:sponsor="goalSponsor"
					:sponsor-link="goalSponsorLink"
					sponsoredByText="Dieses Tor wird präsentiert von"
					align="right"
					background="clear"
					text="white"
				)

</template>

<script>
import TimelineMessageMedia from './TimelineMessageMedia'
import TimelineMessagePlayers from './TimelineMessagePlayers'
import SponsoredBy from './SponsoredBy'

import actionMixin from '../mixins/actionMixin'

import bellIcon from './../assets/img/icons/bell.svg'
import whistleIcon from './../assets/img/icons/whistle.svg'

export default {
	mixins: [actionMixin],
	components: {
		TimelineMessageMedia,
		TimelineMessagePlayers,
		SponsoredBy
	},
	props: {
		id: {
			type: String,
			required: false
		},
		headline: {
			type: String,
			required: false,
			default: null
		},
		message: {
			type: String,
			required: true,
			default: ''
		},
		public: {
			type: Boolean,
			required: false,
			default: true
		},
		time: {
			type: Object,
			default: null,
			required: false
		},
		media: {
			type: Array,
			default: () => [],
			required: false
		},
		players: {
			type: Array,
			default: () => [],
			required: false
		},
		action: String,
		transitionAction: String,
		club: String,

		preview: {
			type: Boolean
		},
		editable: {
			type: Boolean
		},
		mainClub: {
			type: String
		},
		goalSponsor: Object,
		goalSponsorLink: String,
		mainTeamCrest: String,
		opponentTeamCrest: String
	},
	data: () => {
		return {
			slider: null
		}
	},
	computed: {
		messageClasses () {
			var classes = []
			if (this.action) {
				classes.push(`is-action-${this.action}`)
			}
			if (this.hasIcon) {
				classes.push('has-icon')
			} else {
				classes.push('has-no-icon')
			}
			if (this.time) {
				classes.push('has-time')
			}
			if (this.preview) {
				classes.push(`is-preview`)
			}
			if (!this.public) {
				classes.push('is-not-public')
			} else {
				classes.push('is-public')
			}
			if (this.club) {
				if (this.isMainClub) {
					classes.push('is-main-club')
				} else {
					classes.push('is-other-team')
				}
			}
			if (this.action === 'goal' && this.goalSponsor) {
				classes.push('is-sponsored')
			}
			return classes
		},
		messageWithLinebreaks () {
			if (this.message && this.message.replace) {
				return this.message.replace(/(?:\r\n|\r|\n)/g, '<br />')
			}
			return this.message
		},
		isMainClub () {
			return this.club === this.mainClub
		},
		hasIcon () {
			return this.action || this.transitionAction
		},
		actionIcon () {
			if (this.action) {
				return this.getIconForAction(this.action)
			} else if (this.transitionAction) {
				switch (this.transitionAction) {
				case 'warmup-start':
					return bellIcon
				case 'first-half-start':
				case 'first-half-end':
				case 'second-half-start':
				case 'second-half-end':
				case 'first-extra-time-start':
				case 'first-extra-time-end':
				case 'second-extra-time-start':
				case 'second-extra-time-end':
				case 'penalty-shootout-start':
				case 'penalty-shootout-end':
					return whistleIcon
				default: return null
				}
			}
			return null
		},
		crest () {
			return this.isMainClub ? this.mainTeamCrest : this.opponentTeamCrest
		}
	}
}
</script>

<style lang="scss" scoped>
	@import '../style/variables.scss';
	.timeline-message {
		background: white;
		border: 1px solid white;
		overflow: hidden;
		position: relative;
		font-family: 'Bunday Sans', sans-serif;

		p.time {
			text-align: center;
			font-weight: bold;
			font-size: 1.25em;
			.overtime {
				color: $secondary;
				padding-left: 4px;
			}
			&.is-overtime {
					.minute-indicator {
					color: $secondary;
					}
			}
		}

		.timeline-icon {
			width: 16px;
			height: 16px;
			border-radius: 8px;
			background: $timeline;
			z-index: 2;
			position: absolute;
			top: 17px;
			left: 12%;
			margin-left: -8px;
		}
		.message-wrapper {
			display: flex;
			width: 100%;
			.left-column {
				flex-shrink: 0;
				width: 12%;
				padding: 10px;
			}
			.right-column {
				flex-grow: 1;
				overflow: hidden;
				padding: 10px 20px;
			}
		}

		.buttons {
			display: none;
			position: absolute;
			top: 1em;
			right: 1em;

			z-index: 10;
		}
		&:hover .buttons{
			display: block;
		}

		.message-content {
			padding-right: 1em;
			padding-left: 1em;
			min-height: 24px;
			.content {
				margin-bottom: 0;

				// iFrame min height
				::v-deep iframe {
					min-height: 315px;
				}
			}
		}

		&.is-preview {
			box-shadow: 1px 3px 10px #10267a;
			margin-bottom: 3em;
		}
		&.is-not-public {
			border: 1px dashed darkgray;
			&.is-preview {
				box-shadow: 1px 3px 10px darkgray;
			}
		}

		&.is-action-goal,
		&.is-action-own-goal {
			margin-top: 2em;
			margin-bottom: 2em;
		}

		&.is-action-goal.is-main-club,
		&.is-action-own-goal:not(.is-main-club) {
			background-color: #19335a;
			background-image: url('../assets/img/goal-background.jpg');
			background-size: cover;
			background-position: center top;
			color: white;
			.content h4 {
				color: white;
			}
		}

		.video-assist {
			margin-top: 0.5em;
		}

		&.is-sponsored {
			.sponsored-by {
				padding-top: 1em;
				display: flex;
				justify-content: flex-end;
				@media screen and (max-width: 768px) {
					padding-left: 5px;
					padding-right: 5px;
					justify-content: center;
					align-items: flex-start;
				}
			}
		}

		button.draggable-handle {
			cursor: ns-resize;
		}

		@media screen and (max-width: 768px) {
			.timeline-icon {
				width: 32px;
				height: 32px;
				margin-left: -16px;
				border-radius: 16px;
				background: white;
				left: 20px;
			}

			&.is-action-goal.is-main-club .timeline-icon {
				background: white;
			}

			.action-icon {
				position: absolute;
				left: 20px;
				z-index: 99;
				width: 24px;
				height: 24px;
				margin-left: -12px;
				top: 21px;
				z-index: 3;
				img {
					width: 24px;
					height: 24px;
				}
			}
			&.has-no-icon .timeline-icon::before {
				position: absolute;
				content: '';
				background: $timeline;
				width: 18px;
				height: 18px;
				top: 7px;
				left: 7px;
				border-radius: 9px;
				z-index: 3;
			}
			.message-wrapper {
				.left-column {
					width: 36px;
				}
				.right-column {
					padding: 10px 5px;
					padding-left: 0;
				}
			}
			.message-content {
				padding-left: 5px;
				padding-right: 5px;
				padding-top: 13px;
			}

			&.has-time {
				.time {
					position: absolute;
					left: 41px;
					top: 18px;
				}
				.message-content {
					padding-top: 50px;
				}
			}
		}
	}

</style>
